import type { HeartbeatState } from '@didto/domain';
import { useEffect } from 'react';
import { terminalHeartbeat } from '@/lib/api';
import { useDeviceStore } from '@/lib/deviceStore';
import { outbox } from '@/lib/outbox';
import { report } from '@/lib/reporter';

/** Enrolled devices report a heartbeat so the admin panel can show who is online. */
export function useHeartbeat(enabled: boolean, intervalSeconds: number, onRevoked: () => void) {
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    const beat = async () => {
      try {
        // ADR-024: what this tablet still has to send, so a count on its drawer elsewhere can
        // wait for it (pending: being sent; parked: waiting for someone not signed in here, held
        // behind such a command, or refused and kept as "payment not recorded")
        const box = outbox.status();
        const state: HeartbeatState = {
          online: navigator.onLine,
          session: !!useDeviceStore.getState().sessionToken,
          pending: box.pending - box.parked - box.held,
          parked: box.parked + box.held + box.failed,
        };
        const r = await terminalHeartbeat({ ...state });
        if (!stop && r.revoked) onRevoked();
      } catch (e) {
        report().captureMessage('heartbeat failed', { error: String(e) });
      }
    };
    void beat();
    const id = setInterval(() => void beat(), Math.max(15, intervalSeconds) * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalSeconds, onRevoked]);
}
